import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { AppContext } from '../../App';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import {
  DocumentDataExclusionReasons,
  DocumentDataStatusFilters,
  USER_SELECTABLE_DOCUMENT_DATA_STATUSES,
} from '../../types/packager-types';
import {
  SYSTEM_OWNED_EXCLUSION_REASONS,
  renderExclusionReasonMenuItems,
} from '../common/exclusionReasonMenuItems';
import { useDocumentDataUpdate } from '../hooks/documentData/useDocumentDataUpdate';
import { useDocumentDataCreate } from '../hooks/documentData/useDocumentDataCreate';
import { useDocumentDataList } from '../hooks/documentData/useDocumentDataList';
import { extractApiError } from '../../utils/extractApiError';

export interface StatusEditDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentDataId: string | null;
  currentDocumentDataStatus?: DocumentDataStatusFilters | null;
  currentExclusionReason?: string | null;
  currentEscalationDetail?: string | null;
  title?: string;
}

const NO_SELECTION = '';
const DEFAULT_TITLE = 'Update Document Data Status';

const StatusEditDialog: React.FC<StatusEditDialogProps> = ({
  open,
  onClose,
  documentId,
  documentDataId,
  currentDocumentDataStatus,
  currentExclusionReason,
  currentEscalationDetail,
  title = DEFAULT_TITLE,
}) => {
  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();

  const initialDDStatus = (currentDocumentDataStatus ?? '') as DocumentDataStatusFilters | '';
  const initialReason =
    (currentExclusionReason as DocumentDataExclusionReasons) ||
    DocumentDataExclusionReasons.OUT_OF_SCOPE;
  const initialEscalation = currentEscalationDetail || '';

  const [ddStatus, setDDStatus] = React.useState<DocumentDataStatusFilters | ''>(initialDDStatus);
  const [exclusionReason, setExclusionReason] =
    React.useState<DocumentDataExclusionReasons>(initialReason);
  const [escalationDetail, setEscalationDetail] = React.useState<string>(initialEscalation);

  React.useEffect(() => {
    if (open) {
      setDDStatus(initialDDStatus);
      setExclusionReason(initialReason);
      setEscalationDetail(initialEscalation);
    }
    // Re-sync when reopened or when the row's underlying values change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentDocumentDataStatus, currentExclusionReason, currentEscalationDetail]);

  const isDDExcluding = ddStatus === DocumentDataStatusFilters.EXCLUDED;
  const isDDEscalating = ddStatus === DocumentDataStatusFilters.ESCALATED;
  const showExclusionReason = isDDExcluding;
  const currentStatusIsSystemManaged =
    !!currentDocumentDataStatus &&
    !USER_SELECTABLE_DOCUMENT_DATA_STATUSES.includes(currentDocumentDataStatus);

  // Manifest rows sometimes omit document_data_id even when DD exists. Look it up by document
  // when missing so the DD branch UPDATEs the existing row instead of POSTing a duplicate.
  const { data: documentDataList } = useDocumentDataList({
    documentId,
    enabled: open && !documentDataId,
  });
  const effectiveDocumentDataId = documentDataId || documentDataList?.results?.[0]?.id || null;

  const { mutateAsync: updateDocumentData, isPending: isUpdatingDD } = useDocumentDataUpdate({
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to update document data status.'),
      });
    },
  });
  const { mutateAsync: createDocumentData, isPending: isCreatingDD } = useDocumentDataCreate({
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to create document data.'),
      });
    },
  });

  const isPending = isUpdatingDD || isCreatingDD;

  const ddStatusChanged = ddStatus !== initialDDStatus && ddStatus !== NO_SELECTION;
  const reasonChanged =
    showExclusionReason && (exclusionReason as string) !== (currentExclusionReason || '');
  const escalationChanged =
    isDDEscalating && escalationDetail.trim() !== (currentEscalationDetail || '').trim();

  const escalationMissing = isDDEscalating && !escalationDetail.trim();

  const canSubmit =
    !isPending && !escalationMissing && (ddStatusChanged || reasonChanged || escalationChanged);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const ddTargetStatus = ddStatus as DocumentDataStatusFilters;
    const mutation = effectiveDocumentDataId
      ? updateDocumentData({
          docDataId: effectiveDocumentDataId,
          status: ddTargetStatus,
          ...(isDDExcluding ? { exclusionReason } : {}),
          ...(isDDEscalating ? { escalationDetail: escalationDetail.trim() } : {}),
        })
      : createDocumentData({
          document: documentId,
          status: ddTargetStatus,
          ...(isDDExcluding ? { exclusionReason } : {}),
          ...(isDDEscalating ? { escalationDetail: escalationDetail.trim() } : {}),
        });

    const result = await Promise.allSettled([mutation]);

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['documents'] }),
      queryClient.invalidateQueries({ queryKey: ['allExtractions'] }),
      queryClient.invalidateQueries({ queryKey: ['allDocuments'] }),
      queryClient.invalidateQueries({ queryKey: ['extractionBatch'] }),
      queryClient.invalidateQueries({ queryKey: ['extraction'] }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['documents'] }),
      queryClient.refetchQueries({ queryKey: ['allExtractions'] }),
    ]);

    if (result[0].status === 'fulfilled') {
      setToast({ variant: 'success', open: true, message: 'Status updated.' });
      onClose();
    }
    // Per-mutation onError toasts surface failures; leave the dialog open so the user can retry.
  };

  return (
    <DialogGlynt open={open} closeModal={onClose} width="420px" cypressId="dialog-status-edit">
      <DialogTitleGlynt title={title} closeModal={onClose} />
      <DialogContentGlynt>
        <FormControlStyled fullWidth>
          <FormLabelStyled>DOCUMENT DATA STATUS</FormLabelStyled>
          <Select
            value={ddStatus}
            onChange={(e) => setDDStatus(e.target.value as DocumentDataStatusFilters)}
            variant="outlined"
            size="small"
            data-cy="select-status-edit-document-data-status"
          >
            {currentStatusIsSystemManaged && (
              <MenuItem value={currentDocumentDataStatus!} disabled>
                {currentDocumentDataStatus!.replace(/_/g, ' ')} (SYSTEM MANAGED)
              </MenuItem>
            )}
            {USER_SELECTABLE_DOCUMENT_DATA_STATUSES.map((s) => (
              <MenuItem
                key={s}
                value={s}
                data-cy={`status-edit-document-data-status-${s.toLowerCase()}`}
              >
                {s.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControlStyled>

        {showExclusionReason && (
          <FormControlStyled fullWidth>
            <FormLabelStyled>EXCLUSION REASON *</FormLabelStyled>
            <Select
              value={exclusionReason}
              onChange={(e) => setExclusionReason(e.target.value as DocumentDataExclusionReasons)}
              variant="outlined"
              size="small"
              data-cy="select-status-edit-exclusion-reason"
            >
              {renderExclusionReasonMenuItems({
                // Keep the current value selectable or MUI renders the Select blank; a
                // real split parent legitimately holds CONCATENATED.
                omit: SYSTEM_OWNED_EXCLUSION_REASONS.filter((r) => r !== initialReason),
              })}
            </Select>
          </FormControlStyled>
        )}

        {isDDEscalating && (
          <FormControlStyled fullWidth>
            <FormLabelStyled>ESCALATION DETAIL *</FormLabelStyled>
            <TextField
              value={escalationDetail}
              onChange={(e) => setEscalationDetail(e.target.value)}
              placeholder="Why is this being escalated?"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              size="small"
              data-cy="input-status-edit-escalation-detail"
            />
          </FormControlStyled>
        )}

        <Box sx={{ height: 4 }} />
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="btn-status-edit-cancel"
        submitCypressId="btn-status-edit-submit"
        clearCypressId="btn-status-edit-clear"
        submitButtonText="Update"
        submitLoading={isPending}
        showCancelButton={true}
        showClearButton={false}
        submitDisabled={!canSubmit}
      />
    </DialogGlynt>
  );
};

export default StatusEditDialog;
